import { useGetExpenseRegularizationEligibilityQuery } from "src/graphql-next/generated/queries";
import { FeatureFlagNamesEnum } from "src/graphql/generated/types";
import { decodeBase64UUID } from "src/utils";

import { useCurrentAccount } from "./useCurrentAccount";
import { useHasFeatureFlagActivated } from "./useHasFeatureFlagActivated";
import { useUuid } from "./useUuid";

type ExpenseRegularizationEligibility = {
  isEligible: boolean;
  eligibleBuildingIds: Array<string>;
  isBannerEligible: boolean;
};

/*
Landlord-only eligibility for the "Documents de copropriété" category (external expense
regularization / RCL upload). The query is only fired for landlords with the feature flag on;
otherwise it stays disabled and the account is considered not eligible.
*/
export const useExpenseRegularizationEligibility =
  (): ExpenseRegularizationEligibility => {
    const uuid = useUuid();
    const currentAccount = useCurrentAccount();
    const isLandlord = currentAccount.__typename === "LandlordAccount";

    const isExpenseRegularizationUploadEnabled = useHasFeatureFlagActivated(
      FeatureFlagNamesEnum.CustomerPortalUploadExternalExpenseRegularization,
    );

    const { accountId } = decodeBase64UUID(uuid);

    const { data } = useGetExpenseRegularizationEligibilityQuery(
      { accountId },
      { enabled: isLandlord && isExpenseRegularizationUploadEnabled },
    );

    const account = data?.account;
    const eligibility =
      account?.__typename === "LessorAccount"
        ? account.externalExpenseRegularization
        : undefined;

    return {
      isEligible: eligibility?.isEligible ?? false,
      eligibleBuildingIds: eligibility?.eligibleBuildingIds ?? [],
      isBannerEligible: eligibility?.isBannerEligible ?? false,
    };
  };
